import { v4 as uuid } from 'uuid';
import createTracking, { TrackingFunction } from './errorTracking';

type WebSocketEvent = Event | CloseEvent | MessageEvent;

/**
 * Returns the git SHA of the commit the deployment was triggered by,
 * falling back to "<unknown>" if the SHA is not available.
 * @see https://vercel.com/docs/concepts/projects/environment-variables#system-environment-variables
 */
const getReleaseId = () =>
  `${process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA || '<unknown>'}`;

const getCode = (event: WebSocketEvent) =>
  event instanceof CloseEvent ? String(event.code) : null;

const stringifyError = (event: WebSocketEvent): string => {
  const code = getCode(event);
  return code
    ? `Web socket connection closed with code: ${code}`
    : `Web socket connection closed with event: ${event.type}`;
};

export class TransferWebSocketError extends Error {
  name = 'TransferWebSocketError';

  /** Unique generated ID for this instance of the error  */
  referenceId: string;

  /** WebSocket event associated with the error */
  event: WebSocketEvent;

  /** Payload associated with opening with web socket connection. */
  params: unknown;

  trackEvent: TrackingFunction;

  constructor(params: unknown, event: WebSocketEvent) {
    super(stringifyError(event));

    // https://github.com/Microsoft/TypeScript/wiki/Breaking-Changes#extending-built-ins-like-error-array-and-map-may-no-longer-work
    Object.setPrototypeOf(this, TransferWebSocketError.prototype);

    this.trackEvent = createTracking({ release_id: getReleaseId() });

    this.referenceId = uuid();
    this.event = event;
    this.params = params;

    this.trackEvent({
      event_reference_id: this.referenceId,
      error_id: null,
      error_name: this.name,
      error_message: this.message,
      error_title: null,
      error_detail: null,
      error_code: getCode(event),
      error_status: null,
      error_meta: JSON.stringify(params),
      request_url: null,
    });
  }

  get detail() {
    return this.message;
  }

  toJSON() {
    return {
      name: this.name,
      message: this.message,
      referenceId: this.referenceId,
      code: getCode(this.event),
      params: this.params,
    };
  }
}
